/**
 * Game API - Facade for UI to interact with game systems
 *
 * This is the ONLY game import the UI layer should use.
 * Keeps UI isolated from game internals.
 */

import { type AutoPilotStart, cancelAutoPilot, startAutoPilot } from '../game/AutoPilot';
import {
  showContextMenu as chatShowContextMenu,
  submitChatMessage as chatSubmitChatMessage,
  toggleIgnorePlayer as chatToggleIgnorePlayer,
  votemutePlayer as chatVotemutePlayer,
  whisperPlayer as chatWhisperPlayer,
} from '../game/Chat';
import { DISPLAY_DEFAULTS, type DisplaySettingValues, GAME_DEFAULTS, type GameSettingValues } from '../game/Config';
import {
  ALT_SCALE_DEFAULT,
  MINIMAP_SCALE_MAX,
  MINIMAP_SCALE_MIN,
  type PlaneType,
  SCALE_DEFAULT,
} from '../game/Constants';
import { hide as debugHide } from '../game/Debug';
import {
  clear as hitchLogClear,
  start as hitchLogStart,
  stop as hitchLogStop,
  text as hitchLogAsText,
} from '../game/HitchLog';
import { setMountainTint } from '../game/Doodads';
import { start as gamesStart, switchGame as gamesSwitchGame } from '../game/Games';
import {
  getActiveRendererType,
  applyExtendMapEdgesVisibility as graphicsApplyExtendMapEdgesVisibility,
  refreshAllNameplateScales as graphicsRefreshAllNameplateScales,
  toggleFullscreen as graphicsToggleFullscreen,
  setCtfSpawnLinesEnabled,
  setGridVisible,
  setMapFillVisible,
  setOutlinesVisible,
} from '../game/Graphics';
import {
  cancelBindEdit as inputCancelBindEdit,
  clearBind as inputClearBind,
  disableCruiseMode as inputDisableCruiseMode,
  refreshKeybindsUIState as inputRefreshKeybindsUIState,
  resetBinds as inputResetBinds,
  setMouseMode as inputSetMouseMode,
  startBindEdit as inputStartBindEdit,
  toggleCruiseMode as inputToggleCruiseMode,
  touchAction as inputTouchAction,
  touchButton as inputTouchButton,
  touchCancelRelease as inputTouchCancelRelease,
  touchJoystickEnd as inputTouchJoystickEnd,
  touchJoystickMove as inputTouchJoystickMove,
} from '../game/Input';
import { logout as loginLogout, popupLogin } from '../game/Login';
import {
  reconnect as networkReconnect,
  spectateNext as networkSpectateNext,
  spectatePrev as networkSpectatePrev,
} from '../game/Network';
import { forEach as playersForEach } from '../game/Players';
import { setProwlerAlertDevOverride } from '../game/ProwlerAlert';
import {
  radioGetMessages,
  radioHide,
  radioRestoreAll,
  radioRestoreChannel,
  radioSaveConfig,
  radioSendMessage,
} from '../game/Radio';
import { setSettings, settings } from '../game/Settings';
import { toggle as soundToggle, UIClick as soundUIClick } from '../game/Sound';
import type { IdentityProviderId } from '../game/types/Auth';
import type { BindName, BindSlot } from '../game/types/Input';
import {
  serverMessage,
  applyMinimapScale as uiApplyMinimapScale,
  applyScalingFactor as uiApplyScalingFactor,
  closeAllPanels as uiCloseAllPanels,
  generateInviteLink as uiGenerateInviteLink,
  minimapLayout,
  openMainMenu as uiOpenMainMenu,
  resizeGraphicsNow as uiResizeGraphicsNow,
  selectAircraft as uiSelectAircraft,
  selectUpgrade as uiSelectUpgrade,
  setActivePanel as uiSetActivePanel,
  setMinimapScale as uiSetMinimapScale,
  setScalingFactor as uiSetScalingFactor,
  startScorePolling as uiStartScorePolling,
  toggleScaleAltMode as uiToggleScaleAltMode,
  visibilityLaserPointer as uiVisibilityLaserPointer,
} from '../game/UI';
import { start as gamesDataStart, stop as gamesDataStop } from '../services/GamesData';
import { saveString } from '../services/LocalStorage';
import {
  uiState,
  type IdentityProvider,
  type MenuPage,
  type PanelType,
  type RadioChannel,
  type TopBarPanel,
} from './UIState';

/** Provider name to ID mapping for OAuth */
const PROVIDER_IDS: Readonly<Record<IdentityProvider, IdentityProviderId>> = {
  microsoft: 1,
  google: 2,
  twitter: 3,
  reddit: 4,
  twitch: 5,
};

/** The minimap scale's limits, as the menu's slider shows them */
export interface MinimapScaleLimits {
  readonly min: number;
  readonly max: number;
}

/** One object for every menu render, rather than one allocated per call */
const MINIMAP_SCALE_LIMITS: MinimapScaleLimits = { min: MINIMAP_SCALE_MIN, max: MINIMAP_SCALE_MAX };

/**
 * A saved settings group as the facade handles it. A change is saved, mirrored
 * into uiState for the menu and applied to the game through the group's
 * effects; the mirror is filled from the saved values at boot and again when
 * the account's copy arrives. A new setting is its value and default in
 * Config.ts and its control in the menu drawer, plus an entry in `effects`
 * when the game has to act on the change.
 */
interface SettingsGroup<V extends object> {
  /** The saved group: only the settings the player has changed */
  readonly saved: () => Partial<V> | undefined;
  /** Saves the group back whole */
  readonly save: (next: Partial<V>) => void;
  /** What each setting is until changed */
  readonly defaults: V;
  /**
   * The values a setting that names a mode can hold. A saved value outside
   * the list, a retired mode or a hand edit, reads as the default, as the
   * game's own readers of these settings treat it.
   */
  readonly options: { readonly [K in keyof V]?: readonly V[K][] };
  /** The group's reactive copy in uiState */
  readonly mirror: () => V;
  /** What a change does in the game once it is saved and mirrored */
  readonly effects: { readonly [K in keyof V]?: (value: V[K]) => void };
}

/** The saved value of a setting, or its default */
function readSetting<V extends object, K extends keyof V>(group: SettingsGroup<V>, key: K): V[K] {
  const saved = group.saved()?.[key];
  if (saved === undefined) {
    return group.defaults[key];
  }
  const options = group.options[key];
  return options !== undefined && !options.includes(saved) ? group.defaults[key] : saved;
}

/** Saves one setting, mirrors it and applies it */
function setSetting<V extends object, K extends keyof V>(group: SettingsGroup<V>, key: K, value: V[K]): void {
  const next: Partial<V> = { ...group.saved() };
  next[key] = value;
  group.save(next);
  group.mirror()[key] = value;
  group.effects[key]?.(value);
}

/** Fills the group's mirror from the saved values */
function syncSettings<V extends object>(group: SettingsGroup<V>): void {
  const mirror = group.mirror();
  for (const key in group.defaults) {
    mirror[key] = readSetting(group, key);
  }
}

/** Runs the effects of the group's settings whose mirrored value differs from the one in `before`, `skip` aside */
function runChangedEffects<V extends object>(
  group: SettingsGroup<V>,
  before: Readonly<V>,
  skip: ReadonlySet<keyof V>,
): void {
  const mirror = group.mirror();
  for (const key in group.defaults) {
    if (skip.has(key)) continue;
    const value = mirror[key];
    if (value !== before[key]) group.effects[key]?.(value);
  }
}

/** Game settings whose effect is not run when the account's settings arrive: the laser's shows the line at once,
 *  which the spawn does on its own from the saved value, and in the lobby it would draw over the title reel */
const NOT_ON_ACCOUNT_SYNC: ReadonlySet<keyof GameSettingValues> = new Set(['laserPointer']);

/** Rebuilds every nameplate's text: the rank and the teammate health are part of it */
function refreshNameplateTexts(): void {
  playersForEach((p) => p.updateNameplateText(p.rank));
}

/** This device's display settings: the renderer, the theme and its Hit Circles options */
const DISPLAY_SETTINGS: SettingsGroup<DisplaySettingValues> = {
  saved: () => settings.display,
  // Saved over the group, which also holds the minimap scale and the chat size
  save: (next) => setSettings({ display: { ...settings.display, ...next } }),
  defaults: DISPLAY_DEFAULTS,
  // The retired 'auto' renderer value reads as the default, as setupPixiRenderer reads it
  options: { rendererPreference: ['webgpu', 'webgl'], theme: ['default', 'hitshapes'] },
  mirror: () => uiState.displaySettings,
  // The renderer, the theme and HiDPI take effect on the next load; the menu shows a restart badge
  effects: {
    outlines: setOutlinesVisible,
    grid: setGridVisible,
    mountainTint: setMountainTint,
    mapFill: setMapFillVisible,
  },
};

/** The game settings, which the account syncs across devices */
const GAME_SETTINGS: SettingsGroup<GameSettingValues> = {
  saved: () => settings.game,
  save: (next) => setSettings({ game: next }),
  defaults: GAME_DEFAULTS,
  // The removed 'neutral' SOCD mode reads as off, as Input.ts reads it
  options: { socdMode: ['off', 'lastInput'], controlScheme: ['pointToAim', 'directional'] },
  mirror: () => uiState.gameSettings,
  effects: {
    // The LASER key's toggle in UI.ts saves, mirrors and shows on its own, as game code can't reach the facade
    laserPointer: uiVisibilityLaserPointer,
    prowlerAlert: (enabled) => {
      if (!enabled) {
        uiState.prowlerAlertActive = false;
      }
    },
    ctfSpawnLines: setCtfSpawnLinesEnabled,
    extendMapEdges: graphicsApplyExtendMapEdgesVisibility,
    // The full resize path, so the width cap and canvas centering (or their removal) apply now rather than on
    // the next window resize, with the server's zoom cap re-mirrored, since it depends on the viewport's shape
    squaredScene: uiResizeGraphicsNow,
    extendedNameplates: refreshNameplateTexts,
    nameplateRank: refreshNameplateTexts,
    teammateHealth: refreshNameplateTexts,
    scaleNameplates: graphicsRefreshAllNameplateScales,
  },
};

/**
 * Puts text on the clipboard: through the async clipboard where it exists,
 * which is secure contexts only (https, localhost), and otherwise through a
 * hidden textarea and execCommand, the one copy path over LAN http and on
 * older iOS. Resolves false when neither worked.
 */
async function writeClipboardText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission refused or a stale gesture: the fallback below gets a try
    }
  }

  // setSelectionRange is required on iOS, where select() alone creates no selection
  let textarea: HTMLTextAreaElement | null = null;
  try {
    textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    // Removed even if select() or execCommand() threw, so the textarea can't orphan in the DOM
    textarea?.remove();
  }
}

/** Server connection information for starting a game */
export interface ServerInfo {
  /** Server hostname (e.g., "game.airmash.online") */
  host: string;
  /** Server path (e.g., "/ffa1") */
  path: string;
  /** Room ID (e.g., "ffa1") */
  roomId: string;
  /** Room display name (e.g., "FFA #1") */
  roomName: string;
  /** Region ID (e.g., "eu"): the resolved region, never "closest" */
  regionId: string;
  /** Region display name (e.g., "Europe") */
  regionName: string;
  /** The region to remember for next session: "closest" or `regionId` */
  regionPreference: string;
}

export interface GameAPIType {
  /** Update player name (persists to localStorage) */
  setPlayerName(name: string): void;
  /** Start the game with selected server */
  play(name: string, server: ServerInfo): void;
  /** Open OAuth login popup for the specified provider */
  login(providerId: IdentityProvider): void;
  /** Log out the current user (clears auth and reloads) */
  logout(): void;
  /** Show a panel (closes any currently open panel) */
  showPanel(panel: PanelType): void;
  /** Show the help panel, optionally in condensed mode for first play */
  showHelp(condensed?: boolean): void;
  /** Close the currently open panel */
  closePanel(): void;
  /** Switch the open menu drawer to one of its pages */
  showMenuPage(page: MenuPage): void;
  /** Switch to a different game within the same region */
  switchGame(gameServerId: string): void;
  /** Go back to lobby to change region */
  changeRegion(): void;
  /** Reload the page, applying settings that only take effect at startup (theme, renderer) */
  reload(): void;
  /** Start the games data service (refresh + ping) */
  startGamesData(): void;
  /** Stop the games data service */
  stopGamesData(): void;
  /** Clear the current invite banner */
  clearInvite(): void;
  /** Play UI click sound */
  playClick(): void;
  /** Select aircraft type for respawn (1-5) */
  selectAircraft(type: PlaneType): void;
  /** Apply upgrade to a category (1=speed, 2=defense, 3=energy, 4=missile) */
  selectUpgrade(upgradeIndex: 1 | 2 | 3 | 4): void;
  /** Toggle fullscreen mode */
  toggleFullscreen(): void;
  /** Toggle sound on/off, as the SOUND key does; Sound mirrors the state for the toolbar's icon */
  toggleSound(): void;
  /** Toggle the main menu drawer, closing any other panel or popover as it opens */
  toggleMainMenu(): void;
  /**
   * Mirror the saved settings into uiState for the menu: the display and game
   * groups, the zoom, and the fullscreen state. The boot runs it once the
   * loaded settings are applied, and again when the account's settings arrive;
   * every setter mirrors its own change.
   */
  syncState(): void;
  /** The account's settings have replaced the loaded ones (Login): mirrors them, and runs the effects of the game
   *  settings that changed, which the graphics' caches (the map edges, the squared scene) and the nameplates do
   *  not pick up from a live read */
  syncAccountSettings(): void;
  /** Start editing a keybind slot: the next key pressed fills it */
  startBindEdit(bindName: BindName, bindIndex: BindSlot): void;
  /** Cancel the keybind edit in progress, leaving the slot as it was */
  cancelBindEdit(): void;
  /** Unbind one keybind slot, ending any edit in progress */
  clearBind(bindName: BindName, bindIndex: BindSlot): void;
  /** Reset all keybinds to defaults */
  resetKeybinds(): void;
  /** Check if currently editing a keybind */
  isEditingKeybind(): boolean;
  /** Copy the invite link to the clipboard. Resolves false when there is no
   *  link or no copy path worked (permissions, insecure context) — the caller
   *  must not claim "copied" in that case. */
  copyInviteLink(): Promise<boolean>;

  // --- Score Panel ---

  /** Open the score panel and start 5-second polling */
  showScorePanel(): void;
  /** Close the score panel and stop polling */
  closeScorePanel(): void;

  // --- Debug Panel ---

  /** Hide the debug panel (development only) */
  hideDebugPanel(): void;
  /** Dev-only: start or stop the hitch log, which dev-hitch-panel shows while it runs */
  toggleHitchLog(): void;
  /** Dev-only: empty the hitch log and start its clock again */
  clearHitchLog(): void;
  /** Dev-only: the whole hitch log as text, for the panel's copy */
  hitchLogText(): string;

  // --- Context Menu ---

  /** Show context menu for a player at the specified position */
  showContextMenu(playerId: number, top: number, left: number): void;
  /** Close the context menu */
  closeContextMenu(): void;
  /** Whisper to a player (opens chat with /w command) */
  whisperPlayer(playerId: number): void;
  /** Toggle ignore status for a player */
  toggleIgnorePlayer(playerId: number): void;
  /** Vote to mute a player */
  votemutePlayer(playerId: number): void;

  // --- Chat ---

  /** Submit a chat message for sending or command parsing */
  submitChatMessage(message: string): void;
  /** Close the chat input box */
  closeChatInput(): void;
  /** Minimize the chat box */
  minimizeChat(): void;
  /** Maximize the chat box */
  maximizeChat(): void;
  /** Mobile: open the bottom chat drawer (marks messages read). */
  openMobileChat(): void;
  /** Mobile: close the bottom chat drawer. */
  closeMobileChat(): void;
  /** Mobile: mark the drawer's compose input focused/blurred. Raises the same
   *  uiState.chat.isInputVisible flag the desktop input uses, so Input.ts
   *  treats keystrokes as typing rather than game controls (chatBoxOpen guard)
   *  — without it the window keydown handler preventDefaults every key and
   *  eats the typing. */
  setMobileChatComposing(active: boolean): void;
  /** Copy a chat line's text to the clipboard, with a "Copied" notice once it is there */
  copyChatLine(text: string): void;
  /** Save chat panel dimensions to persistent settings */
  saveChatDimensions(width: number | null, height: number | null): void;
  /** Clear all chat messages */
  clearChat(): void;
  /** Close the debug popup */
  closeDebugPopup(): void;
  /** Reconnect to the game server (clears notification + reconnects) */
  reconnect(): void;
  /** Spectate the next player */
  spectateNext(): void;
  /** Spectate the previous player */
  spectatePrev(): void;

  // --- Radio Quick Messages ---

  /** Hide the radio panel */
  hideRadio(): void;
  /** Send a radio message by index (0-9) from the active channel */
  sendRadioMessage(index: number): void;
  /** Open the radio config panel for a channel */
  showRadioConfig(channel: RadioChannel): void;
  /** Get messages for a channel (for config panel editing) */
  getRadioMessages(channel: RadioChannel): string[];
  /** Save edited messages for a channel */
  saveRadioConfig(channel: RadioChannel, messages: string[]): void;
  /** Restore a single channel to default messages */
  restoreRadioChannel(channel: RadioChannel): void;
  /** Restore all channels to default messages */
  restoreAllRadioMessages(): void;

  // --- Settings: the display and game groups ---

  /**
   * Save a display setting (this device's: the renderer, the theme and its
   * Hit Circles options), mirror it for the menu and apply it. The renderer
   * and the theme take effect on the next load.
   */
  setDisplaySetting<K extends keyof DisplaySettingValues>(key: K, value: DisplaySettingValues[K]): void;
  /** Save a game setting (synced to the account), mirror it for the menu and apply it */
  setGameSetting<K extends keyof GameSettingValues>(key: K, value: GameSettingValues[K]): void;
  /** Get the renderer type actually in use by Pixi */
  getActiveRendererType(): 'webgpu' | 'webgl' | 'unknown';
  /** Dev-only: pin the prowler alert state for visual testing; null resumes normal behavior */
  debugSetProwlerAlertOverride(value: boolean | null): void;

  /** Start the pathfinding autopilot to a world-space destination. */
  startAutoPilot(worldX: number, worldY: number): AutoPilotStart;
  /** Cancel the autopilot, releasing all autopilot-held keys. */
  cancelAutoPilot(): void;

  /**
   * Toggle a single-target minimap track on a player. Passing the currently
   * tracked id clears it; passing a different id switches the track to them.
   * The render side draws a ring around the player's existing minimap dot —
   * no hidden info is exposed. Players clears the track when they leave.
   */
  toggleTrackedPlayer(id: number): void;

  /** Set whether cruise mode (auto-throttle) is enabled; uiState.cruiseMode shows it */
  setCruiseMode(enabled: boolean): void;

  // --- Touch controls (driven by the <touch-controls> Lit component) ---
  /** Touch joystick moved — `angleRadian` is nipplejs' `data.angle.radian`, `force` its magnitude */
  touchJoystickMove(angleRadian: number, force: number): void;
  /** Touch joystick released → stop movement */
  touchJoystickEnd(): void;
  /** Touch fire/special button press (true) / release (false) */
  touchButton(action: 'FIRE' | 'SPECIAL', pressed: boolean): void;
  /** Touch one-shot action button (tap) — e.g. contextual DROP (flag / powerup) */
  touchAction(action: 'DROP'): void;
  /** Release all network keys — touchcancel safety net */
  touchCancelRelease(): void;

  /** Turn mouse mode on or off from the menu (saved; no in-game notice) */
  setMouseMode(enabled: boolean): void;

  // --- Minimap Settings ---

  /** Get current minimap scale */
  getMinimapScale(): number;
  /** Set minimap scale (0.75 to 3.0) */
  setMinimapScale(scale: number): void;
  /** Live-preview minimap scale (resizes, no localStorage write) — pair with setMinimapScale on commit */
  applyMinimapScale(scale: number): void;
  /** The minimap scale's limits, for the menu's slider */
  getMinimapScaleLimits(): MinimapScaleLimits;

  /** Get main zoom level (800-7000) */
  getScalingFactor(): number;
  /** Set main zoom level (800-7000) — persists and syncs server horizon */
  setScalingFactor(value: number): void;
  /** Live-preview main zoom (no localStorage write, no network) — pair with setScalingFactor on commit */
  applyScalingFactor(value: number): void;
  /** Get alternate zoom level (800-7000) */
  getAltScalingFactor(): number;
  /** Set alternate zoom level (800-7000) — persists and syncs server horizon */
  setAltScalingFactor(value: number): void;
  /** Live-preview alternate zoom (no localStorage write, no network) — pair with setAltScalingFactor on commit */
  applyAltScalingFactor(value: number): void;
  /** Get whether alternate zoom mode is active */
  getScalingAltMode(): boolean;
  /** Toggle between main and alternate zoom modes */
  toggleScalingAltMode(): void;

  // --- UI dismiss / panel coordination ---
  /** Dismiss the big-message overlay */
  dismissBigMessage(): void;
  /** Dismiss the CTF match-result banner */
  dismissCtfMatchResult(): void;
  /** Dismiss the BTR match-result banner */
  dismissBtrMatchResult(): void;
  /** Dismiss the level-up notice */
  dismissLevelUpNotice(): void;
  /** Set or clear which top-bar popup is currently open (mutual exclusion) */
  setOpenTopBarPanel(panel: TopBarPanel | null): void;
  /** Dismiss the kill notice if it still matches the given id (id-guarded so a new notice isn't clobbered) */
  dismissKillNoticeIfMatching(id: number): void;
  /** Mark the kill notice as fading if it still matches the given id; points or an upgrade arriving after that open a fresh notice */
  markKillNoticeFading(id: number): void;
  /** Dismiss the death notice if it still matches the given id */
  dismissDeathNoticeIfMatching(id: number): void;
  /** Dismiss the game notification if it still matches the given id */
  dismissGameNotificationIfMatching(id: number): void;
}

export const GameAPI: GameAPIType = {
  setPlayerName(name: string): void {
    uiState.playerName = name;
    saveString('playerName', name);
  },

  play(name: string, server: ServerInfo): void {
    // The lobby's refresh ends here; the in-game selector runs it again while open
    gamesDataStop();

    // Update UI state for in-game components
    uiState.screen = 'game';
    uiState.currentGameId = server.roomId;
    uiState.currentGameName = server.roomName;
    uiState.currentRegionId = server.regionId;
    uiState.currentRegionName = server.regionName;

    // Clear game log and chat for fresh start
    uiState.gameLogEntries = [];
    uiState.chat.messages = [];
    uiState.chat.isInputVisible = false;
    uiState.chat.isMinimized = false;
    uiState.chat.unreadCount = 0;
    uiState.chat.isHintRemoved = false;
    uiState.chat.inputPrefill = null;
    uiState.radio = null;
    uiState.debugPopup = null;
    uiState.gameNotification = null;

    // Start the game (this will connect to the server)
    gamesStart(
      { kind: 'server', regionId: server.regionId, roomId: server.roomId, host: server.host, path: server.path },
      name,
      true,
      server.regionPreference,
    );
  },

  login(providerId: IdentityProvider): void {
    popupLogin(PROVIDER_IDS[providerId]);
  },

  logout(): void {
    loginLogout();
  },

  showPanel(panel: PanelType): void {
    uiCloseAllPanels(panel);
    // Initialize panel-specific state before showing
    if (panel === 'keybinds') {
      inputRefreshKeybindsUIState();
    }
    if (panel === 'invite') {
      uiGenerateInviteLink();
    }
    uiSetActivePanel(panel);
  },

  showHelp(condensed?: boolean): void {
    uiState.helpCondensed = condensed === true;
    this.showPanel('help');
  },

  closePanel(): void {
    uiSetActivePanel(null);
  },

  showMenuPage(page: MenuPage): void {
    uiState.menuPage = page;
  },

  switchGame(gameServerId: string): void {
    gamesSwitchGame(gameServerId);
  },

  changeRegion(): void {
    // Reload page to go back to lobby
    window.location.href = '/';
  },

  reload(): void {
    // Keeps the URL, so dev flags like ?dev survive. Settings are already in
    // localStorage: every setter saves synchronously.
    window.location.reload();
  },

  startGamesData(): void {
    void gamesDataStart();
  },

  stopGamesData(): void {
    gamesDataStop();
  },

  clearInvite(): void {
    uiState.invite = null;
  },

  playClick(): void {
    soundUIClick();
  },

  selectAircraft(type: PlaneType): void {
    uiSelectAircraft(type);
  },

  selectUpgrade(upgradeIndex: 1 | 2 | 3 | 4): void {
    uiSelectUpgrade(upgradeIndex);
  },

  toggleFullscreen(): void {
    graphicsToggleFullscreen();
    // No manual state sync here — the module-level `fullscreenchange` listener
    // (below the GameAPI object) updates `uiState.isFullscreen` for every
    // transition, including this button's.
  },

  toggleSound(): void {
    soundToggle();
  },

  toggleMainMenu(): void {
    if (uiState.activePanel === 'mainmenu') {
      uiSetActivePanel(null);
    } else {
      // Opens the way the MAINMENU keybind does, which closes any other panel
      // and top-bar popover or context menu first, so an open loadout, zoom or
      // selector popover doesn't float above the drawer. The values it shows
      // are current: every setter mirrors its own change, and the account's
      // settings are mirrored as they arrive, so the sync that ran here on
      // every opening is gone.
      uiOpenMainMenu();
    }
  },

  syncAccountSettings(): void {
    const before = { ...uiState.gameSettings };
    this.syncState();
    runChangedEffects(GAME_SETTINGS, before, NOT_ON_ACCOUNT_SYNC);
  },

  syncState(): void {
    // Loose `!= null` so iPhone Safari (no Fullscreen API → `fullscreenElement`
    // is `undefined`) reads as not-fullscreen rather than spuriously true.
    uiState.isFullscreen = document.fullscreenElement != null;
    // iPhone Safari has no Fullscreen API at all; iPad Safari and desktop do.
    // Computed once — capability doesn't change over the session.
    uiState.canFullscreen = 'requestFullscreen' in document.documentElement;
    syncSettings(DISPLAY_SETTINGS);
    uiState.displaySettings.chatWidth = settings.display?.chatWidth ?? null;
    uiState.displaySettings.chatHeight = settings.display?.chatHeight ?? null;
    uiState.zoom.scalingFactor = settings.scalingFactor ?? SCALE_DEFAULT;
    uiState.zoom.altScalingFactor = settings.altScalingFactor ?? ALT_SCALE_DEFAULT;
    uiState.zoom.scalingAltMode = settings.scalingAltMode ?? false;
    syncSettings(GAME_SETTINGS);
  },

  startBindEdit(bindName: BindName, bindIndex: BindSlot): void {
    inputStartBindEdit(bindName, bindIndex);
  },

  cancelBindEdit(): void {
    inputCancelBindEdit();
  },

  clearBind(bindName: BindName, bindIndex: BindSlot): void {
    inputClearBind(bindName, bindIndex);
  },

  resetKeybinds(): void {
    inputResetBinds();
  },

  isEditingKeybind(): boolean {
    return uiState.keybinds.editingBindName !== null;
  },

  copyInviteLink(): Promise<boolean> {
    const link = uiState.inviteLink;
    // With no copy path the panel leaves the link text selectable for a manual copy
    return link ? writeClipboardText(link) : Promise.resolve(false);
  },

  // --- Score Panel ---

  showScorePanel(): void {
    uiCloseAllPanels('score');
    uiStartScorePolling();
    uiSetActivePanel('score');
  },

  closeScorePanel(): void {
    uiSetActivePanel(null);
    // Always close context menu when score panel closes
    uiState.contextMenu = null;
  },

  // --- Debug Panel ---

  hideDebugPanel(): void {
    debugHide();
  },

  toggleHitchLog(): void {
    if (uiState.hitchLogOn) {
      hitchLogStop();
    } else {
      hitchLogStart();
    }
  },

  clearHitchLog(): void {
    hitchLogClear();
  },

  hitchLogText(): string {
    return hitchLogAsText();
  },

  // --- Context Menu ---

  showContextMenu(playerId: number, top: number, left: number): void {
    chatShowContextMenu(playerId, top, left);
  },

  closeContextMenu(): void {
    uiState.contextMenu = null;
  },

  whisperPlayer(playerId: number): void {
    chatWhisperPlayer(playerId);
  },

  toggleIgnorePlayer(playerId: number): void {
    chatToggleIgnorePlayer(playerId);
  },

  votemutePlayer(playerId: number): void {
    chatVotemutePlayer(playerId);
  },

  // --- Chat ---

  submitChatMessage(message: string): void {
    chatSubmitChatMessage(message);
  },

  closeChatInput(): void {
    uiState.chat.isInputVisible = false;
    uiState.chat.inputPrefill = null;
  },

  minimizeChat(): void {
    if (uiState.chat.isMinimized) {
      return;
    }
    if (uiState.chat.isInputVisible) {
      uiState.chat.isInputVisible = false;
      uiState.chat.inputPrefill = null;
    }
    uiState.chat.isMinimized = true;
    uiState.chat.unreadCount = 0;
  },

  maximizeChat(): void {
    if (!uiState.chat.isMinimized) {
      return;
    }
    uiState.chat.isMinimized = false;
  },

  /** Mobile: open the bottom chat drawer. Also clears the desktop unread
   *  counter so the two stay consistent if a session changes form factor. */
  openMobileChat(): void {
    uiState.chat.mobileDrawerOpen = true;
    uiState.chat.unreadCount = 0;
  },

  closeMobileChat(): void {
    uiState.chat.mobileDrawerOpen = false;
  },

  /** Mobile: compose-input focus bridge — see the interface doc. The desktop
   *  game-chat renders nothing on mobile, so flipping its visibility flag has
   *  no visual side effect there. */
  setMobileChatComposing(active: boolean): void {
    uiState.chat.isInputVisible = active;
  },

  copyChatLine(text: string): void {
    // The same two copy paths as the invite link; this had the async clipboard
    // alone, so over LAN http the line's Copy did nothing. No notice when
    // neither worked, as before.
    void writeClipboardText(text).then((copied) => {
      if (copied) {
        serverMessage({ type: 1, text: 'Copied to clipboard', duration: 1000 });
      }
    });
  },

  saveChatDimensions(width: number | null, height: number | null): void {
    setSettings({
      display: {
        ...settings.display,
        chatWidth: width ?? undefined,
        chatHeight: height ?? undefined,
      },
    });
    uiState.displaySettings.chatWidth = width;
    uiState.displaySettings.chatHeight = height;
  },

  clearChat(): void {
    uiState.chat.messages = [];
  },

  closeDebugPopup(): void {
    uiState.debugPopup = null;
  },

  reconnect(): void {
    uiState.gameNotification = null;
    networkReconnect();
  },

  spectateNext(): void {
    networkSpectateNext();
  },

  spectatePrev(): void {
    networkSpectatePrev();
  },

  // --- Radio Quick Messages ---

  hideRadio(): void {
    radioHide();
  },

  sendRadioMessage(index: number): void {
    radioSendMessage(index);
  },

  showRadioConfig(channel: RadioChannel): void {
    uiCloseAllPanels('radioconfig');
    uiState.radioConfigChannel = channel;
    uiSetActivePanel('radioconfig');
  },

  getRadioMessages(channel: RadioChannel): string[] {
    return radioGetMessages(channel);
  },

  saveRadioConfig(channel: RadioChannel, messages: string[]): void {
    radioSaveConfig(channel, messages);
  },

  restoreRadioChannel(channel: RadioChannel): void {
    radioRestoreChannel(channel);
  },

  restoreAllRadioMessages(): void {
    radioRestoreAll();
  },

  // --- Settings: the display and game groups ---

  setDisplaySetting<K extends keyof DisplaySettingValues>(key: K, value: DisplaySettingValues[K]): void {
    setSetting(DISPLAY_SETTINGS, key, value);
  },

  setGameSetting<K extends keyof GameSettingValues>(key: K, value: GameSettingValues[K]): void {
    setSetting(GAME_SETTINGS, key, value);
  },

  getActiveRendererType(): 'webgpu' | 'webgl' | 'unknown' {
    return getActiveRendererType();
  },

  debugSetProwlerAlertOverride(value: boolean | null): void {
    setProwlerAlertDevOverride(value);
  },

  startAutoPilot(worldX: number, worldY: number): AutoPilotStart {
    return startAutoPilot(worldX, worldY);
  },

  cancelAutoPilot(): void {
    cancelAutoPilot();
  },

  toggleTrackedPlayer(id: number): void {
    uiState.trackedPlayerId = uiState.trackedPlayerId === id ? null : id;
  },

  setCruiseMode(enabled: boolean): void {
    // Input mirrors the state; already in the state asked for means nothing to do
    const currentState = uiState.cruiseMode;
    if (enabled && !currentState) {
      inputToggleCruiseMode();
    } else if (!enabled && currentState) {
      inputDisableCruiseMode();
    }
  },

  // --- Touch controls bridge → game-side input (translation/timing stay in Input.ts) ---
  touchJoystickMove(angleRadian: number, force: number): void {
    inputTouchJoystickMove(angleRadian, force);
  },

  touchJoystickEnd(): void {
    inputTouchJoystickEnd();
  },

  touchButton(action: 'FIRE' | 'SPECIAL', pressed: boolean): void {
    inputTouchButton(action, pressed);
  },

  touchAction(action: 'DROP'): void {
    inputTouchAction(action);
  },

  touchCancelRelease(): void {
    inputTouchCancelRelease();
  },

  /** Turn mouse mode on or off from the menu. setMouseMode saves it and
   *  mirrors uiState itself, since the M key reaches it without GameAPI. */
  setMouseMode(enabled: boolean): void {
    inputSetMouseMode(enabled, false);
  },

  // --- Minimap Settings ---

  getMinimapScale(): number {
    return minimapLayout.scale;
  },

  setMinimapScale(scale: number): void {
    uiSetMinimapScale(scale);
  },

  applyMinimapScale(scale: number): void {
    uiApplyMinimapScale(scale);
  },

  getMinimapScaleLimits(): MinimapScaleLimits {
    return MINIMAP_SCALE_LIMITS;
  },

  // --- Zoom Settings ---

  getScalingFactor(): number {
    return settings.scalingFactor || SCALE_DEFAULT;
  },

  setScalingFactor(value: number): void {
    if (settings.scalingAltMode) {
      // Not the active mode: persist and mirror it without applying it
      setSettings({ scalingFactor: value });
      uiState.zoom.scalingFactor = value;
    } else {
      // UI.setScalingFactor persists and mirrors the active mode's value itself
      uiSetScalingFactor(value);
    }
  },

  applyScalingFactor(value: number): void {
    uiState.zoom.scalingFactor = value;
    if (!settings.scalingAltMode) {
      uiApplyScalingFactor(value);
    }
  },

  getAltScalingFactor(): number {
    return settings.altScalingFactor || ALT_SCALE_DEFAULT;
  },

  setAltScalingFactor(value: number): void {
    if (settings.scalingAltMode) {
      // UI.setScalingFactor persists and mirrors the active mode's value itself
      uiSetScalingFactor(value);
    } else {
      // Not the active mode: persist and mirror it without applying it
      setSettings({ altScalingFactor: value });
      uiState.zoom.altScalingFactor = value;
    }
  },

  applyAltScalingFactor(value: number): void {
    uiState.zoom.altScalingFactor = value;
    if (settings.scalingAltMode) {
      uiApplyScalingFactor(value);
    }
  },

  getScalingAltMode(): boolean {
    return settings.scalingAltMode || false;
  },

  toggleScalingAltMode(): void {
    uiToggleScaleAltMode();
  },

  // --- UI dismiss / panel coordination ---

  dismissBigMessage(): void {
    uiState.bigMessage = null;
  },

  dismissCtfMatchResult(): void {
    uiState.ctfMatchResult = null;
  },

  dismissBtrMatchResult(): void {
    uiState.btrMatchResult = null;
  },

  dismissLevelUpNotice(): void {
    uiState.levelUp = null;
  },

  setOpenTopBarPanel(panel: TopBarPanel | null): void {
    uiState.openTopBarPanel = panel;
  },

  dismissKillNoticeIfMatching(id: number): void {
    if (uiState.killNotice && uiState.killNotice.id === id) {
      uiState.killNotice = null;
    }
  },

  markKillNoticeFading(id: number): void {
    if (uiState.killNotice && uiState.killNotice.id === id) {
      uiState.killNotice.fading = true;
    }
  },

  dismissDeathNoticeIfMatching(id: number): void {
    if (uiState.deathNotice && uiState.deathNotice.id === id) {
      uiState.deathNotice = null;
    }
  },

  dismissGameNotificationIfMatching(id: number): void {
    if (uiState.gameNotification && uiState.gameNotification.id === id) {
      uiState.gameNotification = null;
    }
  },
};

// Keep `uiState.isFullscreen` in sync no matter how fullscreen changes — the
// toolbar button, the Escape/F11 keys, or the browser's own exit UI all emit
// `fullscreenchange`. Without this, exiting via anything but the button leaves
// the toolbar icon stuck on "minimize". Harmless where the Fullscreen API is
// absent (iPhone Safari) — the event simply never fires.
document.addEventListener('fullscreenchange', () => {
  uiState.isFullscreen = document.fullscreenElement != null;
});
